import React, { useMemo } from 'react';
import MonthMultiDayEntry from './MonthlySchedule/MonthMultiDayEntry';
import MonthShiftDayEntry from './MonthlySchedule/MonthShiftDayEntry';
import MonthMoreView from './MonthlySchedule/MonthlyMoreView';
import { buildLaneOrderedMultiDay } from './MonthlySchedule/monthSchedule.helpers';

import {
  MONTHLY_ENTRY_HEIGHT,
  MONTHLY_HEADER_HEIGHT,
  MONTHLY_ENTRY_PADDING,
} from './schedule.constants';

const isLaneSpacer = (item) => item && item.__laneSpacer;

const countRealMultiParsed = (parsed) => (
  parsed.filter((item) => !isLaneSpacer(item)).length
);

// Determine shifts to show based off available height of view
const prepareShifts = ({
  layoutMulti,
  interDay,
  height,
  realMultiLen,
}) => {
  let currentHeight = MONTHLY_HEADER_HEIGHT;

  let i = 0;
  const parsedMulti = [];
  const parsedInter = [];
  let moreCount = 0;

  if (height <= currentHeight) return { parsedMulti, parsedInter, moreCount };

  while (i < layoutMulti.length && currentHeight <= height) {
    parsedMulti.push(layoutMulti[i]);
    currentHeight += MONTHLY_ENTRY_HEIGHT;
    i += 1;
  }

  // Too many multi day views, pop latest and replace with "More" view.
  if (currentHeight > height) {
    parsedMulti.pop();
    moreCount = realMultiLen + interDay.length - countRealMultiParsed(parsedMulti);
    return { parsedMulti, parsedInter, moreCount };
  }

  i = 0;
  while (i < interDay.length && currentHeight <= height) {
    parsedInter.push(interDay[i]);
    currentHeight += MONTHLY_ENTRY_HEIGHT;
    i += 1;
  }

  // Too many inter day views, pop latest and replace with "More" view.
  if (currentHeight > height) {
    parsedInter.pop();
  }
  moreCount = (
    realMultiLen
    + interDay.length
    - countRealMultiParsed(parsedMulti)
    - parsedInter.length
  );
  return { parsedMulti, parsedInter, moreCount };
};

export default function ScheduleCalendarDay({
  user,
  day,
  endDate,
  weekStart,
  height,
  onShiftSelect,
  multiDay = [],
  interDay = [],
  laneByShiftId,
  hoverCard = false,
}) {
  const layoutMulti = useMemo(() => {
    const laneOrdered = buildLaneOrderedMultiDay(multiDay, laneByShiftId);
    if (laneOrdered) return laneOrdered;
    return [...multiDay].sort(
      (a, b) => (b.endTime - b.startTime) - (a.endTime - a.startTime),
    );
  }, [multiDay, laneByShiftId]);

  const realMultiLen = multiDay.length;

  const layoutHeight = Number.isFinite(height) ? height - MONTHLY_ENTRY_PADDING : 0;

  const { parsedMulti, parsedInter, moreCount } = useMemo(() => prepareShifts({
    layoutMulti,
    interDay,
    height: layoutHeight,
    realMultiLen,
  }), [layoutMulti, interDay, layoutHeight, realMultiLen]);

  const multiDayForMore = useMemo(
    () => [...multiDay].sort(
      (a, b) => (b.endTime - b.startTime) - (a.endTime - a.startTime),
    ),
    [multiDay],
  );

  return (
    <>
      {
        parsedMulti.map((item) => (
          isLaneSpacer(item) ? (
            <div
              key={item.id}
              className="schedule-month-multiday-placeholder"
              aria-hidden
              style={{ visibility: 'hidden', pointerEvents: 'none' }}
            />
          ) : (
            <MonthMultiDayEntry
              user={user}
              key={item.id}
              shift={item}
              day={day}
              onClick={onShiftSelect}
              weekStart={weekStart}
              endDate={endDate}
              hoverCard={hoverCard}
            />
          )
        ))
      }
      {
        parsedInter.map((shift) => (
          <MonthShiftDayEntry
            user={user}
            key={shift.id}
            shift={shift}
            onClick={onShiftSelect}
            hoverCard={hoverCard}
          />
        ))
      }
      <MonthMoreView
        user={user}
        day={day}
        moreCount={moreCount}
        multiDayShifts={multiDayForMore}
        interDayShifts={interDay}
        onShiftSelect={onShiftSelect}
        hoverCard={hoverCard}
      />
    </>
  );
}
